import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { Button } from '@material-tailwind/react';
import defaultBackground from '../../assets/Record/recordprint.jpg';

interface LayoutText {
  custom_id: string;
  text: string;
  x: number;
  y: number;
  font: string;
  color: string;
}

interface LayoutConfig {
  texts: LayoutText[];
  bg_image?: string;
}

type RecordData = {
  Competition: string;
  Bib: string;
  Name: string;
  Division: string;
  Time: string;
};

const RecordPrint = () => {
  const { compTitle, bibNum } = useParams<{ compTitle?: string; bibNum?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = (location.state as {
    userName?: string;
    compTitle?: string;
    compDivLs?: string;
    compRecord?: string;
    compBib?: string;
    compCode?: number;
  }) || {};

  // 참가자 및 대회 정보
  const [userName, setUserName] = useState(stateData.userName || '이름 없음');
  const [userBib, setUserBib] = useState(stateData.compBib || (bibNum || '-'));
  const [compTitleState, setCompTitleState] = useState(stateData.compTitle || '마라톤 대회');
  const [compDivLs, setCompDivLs] = useState(stateData.compDivLs || 'Full');
  const [compRecord, setCompRecord] = useState(stateData.compRecord || '기록이 없습니다.');
  const [compCodeState, setCompCodeState] = useState<number | null>(stateData.compCode || null);
  useEffect(() => {
    if (stateData.compCode) {
      setCompCodeState(stateData.compCode);
    }
  }, [stateData.compCode]);

  // 레이아웃 정보 (mode=photo)
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null);
  useEffect(() => {
    if (compCodeState) {
      axios.get(`/api/get-layout/${compCodeState}?mode=normal`)
        .then((res) => {
          console.log("Fetched layout:", res.data);
          const raw = res.data;
          let parsedTexts: LayoutText[] = [];
          if (raw.layout_config) {
            try {
              const parsedObj = JSON.parse(raw.layout_config);
              parsedTexts = parsedObj.texts || [];
            } catch(e) {
              console.error("JSON parse error", e);
            }
          }
          const finalData: LayoutConfig = {
            bg_image: raw.bg_image,
            texts: parsedTexts
          };
          setLayoutConfig(finalData);
        })
        .catch((err) => {
          console.error("레이아웃 불러오기 오류:", err);
        });
    }
  }, [compCodeState]);

  // 사용자 업로드 이미지 상태 (증명사진)
  const [userImageUrl, setUserImageUrl] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // 배경 이미지 URL: layoutConfig에서 가져오거나 기본 이미지 사용
  const backgroundUrl = layoutConfig && layoutConfig.bg_image
    ? `/static/images/${layoutConfig.bg_image}`
    : defaultBackground;

  // A4 사이즈 캔버스 (기본값: portrait A4: 595x842)
  const [canvasWidth, setCanvasWidth] = useState(595);
  const [canvasHeight, setCanvasHeight] = useState(842);

  // 배경 이미지의 자연 크기를 읽어 A4 크기 결정 (landscape vs portrait)
  useEffect(() => {
    const img = new Image();
    img.src = backgroundUrl;
    img.onload = () => {
      if (img.naturalWidth > img.naturalHeight) {
        setCanvasWidth(842);
        setCanvasHeight(595);
      } else {
        setCanvasWidth(595);
        setCanvasHeight(842);
      }
    };
  }, [backgroundUrl]);

  // 동적 반응형 스케일링: 화면 너비에 따라 scale 적용
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const handleResize = () => {
      if (canvasWidth) {
        const availableWidth = window.innerWidth - 20;
        const newScale = Math.min(1, availableWidth / canvasWidth);
        setScale(newScale);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [canvasWidth]);

  // 파일 업로드 처리: 사용자 이미지 URL 저장
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadedFile(file);
      const localUrl = URL.createObjectURL(file);
      setUserImageUrl(localUrl);
    }
  };

  const captureCanvas = (callback: (dataUrl: string) => void) => {
    if (canvasRef.current) {
      html2canvas(canvasRef.current, {
        scale: 4,
        useCORS: true,
        onclone: (clonedDoc) => {
          const overlays = clonedDoc.querySelectorAll('.overlay-text');
          overlays.forEach(el => {
            const computedStyle = window.getComputedStyle(el);
            const currentTop = computedStyle.top;
            const topVal = parseFloat(currentTop);
            // 텍스트 위치를 위로 10px 올림
            (el as HTMLElement).style.top = (topVal - 10) + "px";
          });
        }
      }).then(canvas => {
        const dataUrl = canvas.toDataURL("image/png");
        callback(dataUrl);
      });
    }
  };
  


  const handleDownload = () => {
    captureCanvas((dataUrl) => {
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "일반기록증.png";
      link.click();
    });
  };
  const mapping: Record<string, string> = {
    name: userName,
    time: compRecord,
    div: compDivLs,
    bib: userBib,
  };
   // layoutConfig가 없으면 안내 메시지를 출력
   if (layoutConfig === null) {
    return (
      <div className="flex justify-center items-start h-screen bg-white pt-40">
        <h1 className="text-2xl md:text-4xl font-bold text-center text-red-600">
          이번 대회에서는 서비스하지 않습니다.
        </h1>
      </div>
    );
  }
  // 캔버스 컨텐츠 렌더링: 배경, 텍스트 오버레이, 그리고 사용자 이미지 (증명사진)
  const renderContent = () => (
    <div style={{ position: "relative", width: `${canvasWidth}px`, height: `${canvasHeight}px`, overflow: "hidden", backgroundColor: "#fff" }}>
      <img
        src={backgroundUrl}
        alt="background"
        style={{
          position: 'absolute',
          width: '100%',
          height: 'auto',
          left: 0,
          top: 0,
          objectFit: 'cover'
        }}
      />
      {layoutConfig?.texts.map((item, idx) => {
        const [fontName, fontSizeStr] = item.font.split(' ');
        const fontSize = parseInt(fontSizeStr) || 20;
        const displayText = mapping[item.custom_id] ?? item.text ?? item.custom_id;
        return (
          <div
            key={idx}
            className="overlay-text"
            style={{
              position: 'absolute',
              left: item.x,
              top: item.y,
              transform: 'translate(-50%, 0)',
              textAlign: 'center',
              // transform: 'translate(0px,0px)',
              display: 'inline-block',
              // textAlign: 'left',
              fontFamily: fontName,
              fontSize: fontSize,
              color: item.color,
              whiteSpace: 'nowrap'
            }}
          >
            {displayText}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="flex justify-center flex-col items-center">
      {/* 디버그용 */}
      <div style={{ position: 'absolute', top: 0, right: 0, color: 'red', fontSize: '16px' }}>
        compCode: {compCodeState}
      </div>
      {/* 동적 스케일 적용 컨테이너 */}
      <div className="mt-8 mb-8 mx-auto" style={{ width: canvasWidth * scale, height: canvasHeight * scale}}>
        <div
        
          id="canvas-container"
          ref={canvasRef}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: canvasWidth,
            height: canvasHeight,
          }}
        >
          {renderContent()}
        </div>
      </div>
      <div className="flex gap-4 mb-4">
        <Button variant="filled" color="blue" onClick={handleDownload}>
          다운로드
        </Button>
      </div>
    </div>
  );
};

export default RecordPrint;
